import type { FormProps } from "antd"
import { Button, Form, message, Space, Spin } from "antd"
import Title from "antd/es/typography/Title"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"
import { Paths } from "~/constants"
import { t } from "~/languages/i18n"
import { ParentQuestionData, ParentQuestionFieldType, ParentQuestionPayload } from "~/types/types"
import { createParentQuestion, fetchParentQuestionById, updateParentQuestion } from "../services/parent-question"
import ParentQuestionForm from "./ParentQuestionForm"

const ParentQuestionPage: React.FC = () => {
  const [parentQuestion, setParentQuestion] = useState<ParentQuestionData | null>(null)
  const [language, setLanguages] = useState("vi")
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const isEditMode = !!id

  const handleFetchParentQuestion = async (parentQuestionId: number) => {
    try {
      setLoading(true)
      const response = await fetchParentQuestionById(parentQuestionId)
      setParentQuestion(response)

      form.setFieldsValue({
        question: response.attributes.question,
        answer: response.attributes.answer,
        author_name: response.attributes.author_name,
        email: response.attributes.email,
        phone: response.attributes.phone,
        status: response.attributes.status,
        post: response.attributes.post?.data?.id,
        page: response.attributes.page?.data?.id,
      })

      setLanguages(response.language || "vi")
      setLoading(false)
    } catch {
      message.error(t("Error fetching Parent Question"))
      setLoading(false)
    }
  }

  const onFinish: FormProps<ParentQuestionFieldType>["onFinish"] = async values => {
    const dataPayload: ParentQuestionPayload = {
      question: values.question,
      answer: values.answer,
      author_name: values.author_name || null,
      email: values.email || null,
      phone: values.phone || null,
      status: values.status || "PENDING",
    }

    if (values.post) {
      dataPayload.post = values.post
    }
    if (values.page) {
      dataPayload.page = values.page
    }

    try {
      if (isEditMode) {
        await updateParentQuestion(Number(id), dataPayload)
        message.success(t("Parent Question updated successfully!"))
      } else {
        await createParentQuestion(dataPayload)
        message.success(t("Parent Question created successfully!"))
      }
      form.resetFields()
      navigate(Paths.PARENT_QUESTIONS.LIST.PATH)
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error?.message || error?.message || "Unknown error"
      message.error(`${isEditMode ? t("Failed to update Parent Question") : t("Failed to create Parent Question")}: ${errorMessage}`)
    }
  }

  const onFinishFailed: FormProps<ParentQuestionFieldType>["onFinishFailed"] = () => {
    message.error(t("Please check the form and fix any errors."))
  }

  useEffect(() => {
    if (id) {
      handleFetchParentQuestion(Number(id))
    }
  }, [id])

  return (
    <>
      {loading ? (
        <div className="flex items-center justify-center min-h-[500px]">
          <Spin size="large" />
        </div>
      ) : (
        <div className="p-4">
          <Space className="mb-4">
            <Title level={2}>{isEditMode ? t("Edit Parent Question") : t("Add New Parent Question")}</Title>
            {isEditMode && (
              <Button onClick={() => navigate(Paths.PARENT_QUESTIONS.CREATE.PATH)}>{t("Add New")}</Button>
            )}
          </Space>
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <ParentQuestionForm
                form={form}
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                initialSlug={parentQuestion?.attributes.slug}
              />
              <div className="mt-4">
                <Button type="primary" onClick={() => form.submit()} size="large">
                  {isEditMode ? t("Update") : t("Save")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ParentQuestionPage
